import {
  CreateInspectionResponses,
  CreateInspectionTenant,
  EntityDto,
  Inspection,
  InspectionCategory,
  InspectionService,
  PagedInspectionRequest,
  UpdateInspection,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class InspectionStore {
  inspection: Inspection | undefined;
  inspections: Map<string, Inspection> = new Map();
  inspectionCategories: Map<number, InspectionCategory> = new Map();
  tenantCategories: Map<number, InspectionCategory> = new Map();
  totalCount: number = 0;
  skipCount: number = 0;
  currentPage: number = 1;
  maxResultCount: number = 15;
  loading: boolean = false;
  submitResponsesLoading: boolean = false;
  submitCategoriesLoading: boolean = false;
  tenantCategoriesLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(request: PagedInspectionRequest) {
    this.setLoading(true);
    try {
      const result = await InspectionService.getAll(request);
      runInAction(() => {
        this.inspections.clear();
        result.items.forEach((inspection: Inspection) => {
          this.inspections.set(inspection.id, inspection);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async get(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await InspectionService.get(entityDto);
      runInAction(() => {
        this.inspection = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getAllCategories(tenantId: number) {
    this.setLoading(true);
    try {
      const result = await InspectionService.getAllCategories(tenantId);
      runInAction(() => {
        result.forEach((category: InspectionCategory) => {
          this.inspectionCategories.set(category.id, category);
        });
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getAllCategoriesByTenantId(tenantId: number) {
    this.setTenantCategoriesLoading(true);
    try {
      const result = await InspectionService.getAllCategoriesByTenantId(tenantId);
      runInAction(() => {
        result.forEach((category: InspectionCategory) => {
          this.tenantCategories.set(category.id, category);
        });
      });
      this.setTenantCategoriesLoading(false);
    } catch (error) {
      this.setTenantCategoriesLoading(false);
      console.error(error);
    }
  }

  async create(inspection: Inspection) {
    this.setLoading(true);
    try {
      const result = await InspectionService.create(inspection);
      this.setLoading(false);
      runInAction(() => {
        this.inspections.set(result.id, result);
      });
      message.success({
        content: 'Inspection was successfully confirmed!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async update(inspection: UpdateInspection): Promise<Inspection> {
    this.setLoading(true);
    try {
      const result = await InspectionService.update(inspection);
      runInAction(() => {
        this.inspection = { ...this.inspection, isPublished: true } as Inspection;
        this.inspections.set(result.id, {
          ...this.inspections.get(result.id),
          isPublished: true,
        } as Inspection);
      });
      this.setLoading(false);
      message.success({
        content: 'Inspection was successfully published!',
      });
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      throw error;
    }
  }

  async publish(id: EntityDto<string>): Promise<Inspection> {
    this.setLoading(true);
    try {
      const result = await InspectionService.publish(id);
      runInAction(() => {
        this.inspection = { ...this.inspection, isPublished: true } as Inspection;
        this.inspections.set(result.id, {
          ...this.inspections.get(result.id),
          isPublished: true,
        } as Inspection);
      });
      this.setLoading(false);
      message.success({
        content: 'Inspection was successfully published!',
      });
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      throw error;
    }
  }

  async createInspectionTenant(request: CreateInspectionTenant) {
    this.setSubmitCategoriesLoading(true);
    try {
      const result = await InspectionService.createInspectionTenant(request);
      this.setSubmitCategoriesLoading(false);
      message.success({
        content: 'Successfully saved!',
      });
      return result;
    } catch (error) {
      this.setSubmitCategoriesLoading(false);
      console.error(error);
      throw error;
    }
  }
  async createResponses(request: CreateInspectionResponses) {
    this.setSubmitResponsesLoading(true);
    try {
      await InspectionService.createResponses(request);
      this.setSubmitResponsesLoading(false);
      message.success({
        content: 'Inspection responses were successfully saved!',
      });
    } catch (error) {
      this.setSubmitResponsesLoading(false);
      console.error(error);
    }
  }

  async createOrderInspection({ categoryId, orderId }: { categoryId: number; orderId: string }) {
    this.setLoading(true);
    try {
      const result = await InspectionService.createOrderInspection({
        categoryId,
        orderId,
      });
      runInAction(() => {
        this.inspections.set(result.id, result);
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully added!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async sendInspectionEmail(id: string, email?: string) {
    this.setLoading(true);
    try {
      await InspectionService.sendInspectionEmail(id, email);
      this.setLoading(false);
      message.success({
        content: 'Inspection email sent successfully!',
      });
    } catch (error: any) {
      this.setLoading(false);
      message.error(error?.response?.data?.error?.message || 'Failed to send email');
    }
  }

  async deleteOrderInspection({ categoryId, orderId }: { categoryId: number; orderId: string }) {
    this.setLoading(true);
    try {
      const result = await InspectionService.deleteOrderInspection({
        categoryId,
        orderId,
      });

      runInAction(() => {
        this.inspections.delete(result);
      });

      this.setLoading(false);
      message.success({
        content: 'Successfully removed!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSubmitResponsesLoading = (value: boolean) => {
    runInAction(() => {
      this.submitResponsesLoading = value;
    });
  };

  setSubmitCategoriesLoading = (value: boolean) => {
    runInAction(() => {
      this.submitCategoriesLoading = value;
    });
  };

  setTenantCategoriesLoading = (value: boolean) => {
    runInAction(() => {
      this.tenantCategoriesLoading = value;
    });
  };

  clearInspection() {
    runInAction(() => {
      this.inspection = undefined;
    });
  }

  clearInspections() {
    runInAction(() => {
      this.inspections.clear();
    });
  }

  cleartenantCategories() {
    runInAction(() => {
      this.tenantCategoriesLoading = false;
    });
  }

  clearInspectionCategories() {
    runInAction(() => {
      this.inspectionCategories.clear();
    });
  }

  resetInspections = () => {
    runInAction(() => {
      this.inspections.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };
}

export default InspectionStore;
